import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import CountryPage, {
  CountryPageFormData
} from "@saleor/countries/components/CountryPage/CountryPage";
import RegionDeleteDialog from "@saleor/countries/components/RegionDeleteDialog";
import RegionEditDialog, {
  RegionEditDialogFormData
} from "@saleor/countries/components/RegionEditDialog/RegionEditDialog";
import { useCountryCreateMutation } from "@saleor/countries/mutations";
import {
  countryAddUrl,
  CountryAddUrlDialog,
  CountryAddUrlQueryParams,
  countryListUrl,
  countryUrl
} from "@saleor/countries/urls";
import { CountryErrorFragment } from "@saleor/fragments/types/CountryErrorFragment";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { getStringOrPlaceholder } from "@saleor/misc";
import useCurrencySearch from "@saleor/searches/useCurrencySearch";
import useLanguageSearch from "@saleor/searches/useLanguageSearch";
import { ReorderEvent } from "@saleor/types";
import { CountryErrorCode } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import {
  add,
  isSelected,
  move,
  remove,
  updateAtIndex
} from "@saleor/utils/lists";
import React from "react";
import { useIntl } from "react-intl";
interface CountryDetailsProps {
  params: CountryAddUrlQueryParams;
}

function areValuesEqual(
  a: RegionEditDialogFormData,
  b: RegionEditDialogFormData
) {
  return a.name === b.name;
}

const regionsAlreadyExistsError: CountryErrorFragment = {
  __typename: "CountryError",
  code: CountryErrorCode.ALREADY_EXISTS,
  field: "name"
};
const regionsNotNullError: CountryErrorFragment = {
  __typename: "CountryError",
  code: CountryErrorCode.NOT_FOUND,
  field: "name"
};

const CountryDetails: React.FC<CountryDetailsProps> = ({ params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const [values, setValues] = React.useState<RegionEditDialogFormData[]>([]);
  const [textChange, setTextChange] = React.useState<boolean>();
  const [valueErrors, setValueErrors] = React.useState<CountryErrorFragment[]>(
    []
  );
  const [countryCreate, countryCreateOpts] = useCountryCreateMutation({
    onCompleted: data => {
      if (data.countryCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Successfully created country"
          })
        });
        navigate(countryUrl(data.countryCreate.country.id));
      }
    }
  });
  const id = params.id ? parseInt(params.id, 0) : undefined;
  const [openModal, closeModal] = createDialogActionHandlers<
    CountryAddUrlDialog,
    CountryAddUrlQueryParams
  >(navigate, countryAddUrl, params);
  React.useEffect(() => setValueErrors([]), [params.action]);
  const handleRegionsDelete = () => {
    setValues(remove(values[params.id], values, areValuesEqual));
    closeModal();
  };
  const handleRegionsUpdate = (input: RegionEditDialogFormData) => {
    if (isSelected(input, values, areValuesEqual) && textChange) {
      setValueErrors([regionsAlreadyExistsError]);
    } else if (input.name === "") {
      setValueErrors([regionsNotNullError]);
    } else {
      setValues(updateAtIndex(input, values, id));
      setTextChange(false);
      closeModal();
    }
  };
  const handleRegionsCreate = (input: RegionEditDialogFormData) => {
    setTextChange(false);
    if (isSelected(input, values, areValuesEqual)) {
      setValueErrors([regionsAlreadyExistsError]);
    } else if (input.name === "") {
      setValueErrors([regionsNotNullError]);
    } else {
      setValues(add(input, values));
      closeModal();
    }
  };

  const handleRegionsReorder = ({ newIndex, oldIndex }: ReorderEvent) =>
    setValues(move(values[oldIndex], values, areValuesEqual, newIndex));

  const handleCreate = async (data: CountryPageFormData) => {
    const input = {
      ...data,
      defaultCurrency: data.defaultCurrency ? data.defaultCurrency.code : "",
      defaultLanguage: data.defaultLanguage
        ? data.defaultLanguage.languageCode
        : "",
      regions: values.map(value => ({
        name: value.name,
        code: value.code,
        displayOrder: value.displayOrder,
        enable: value.reenable
      }))
    };
    const result = await countryCreate({
      variables: {
        input
      }
    });
    return result?.data.countryCreate?.country?.id || null;
  };
  const handleSubmit = handleCreate;
  const {
    loadMore: loadMoreLanguages,
    search: searchLanguage,
    result: searchLanguageOpts
  } = useLanguageSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const {
    loadMore: loadMoreCurrencyrates,
    search: searchCurrencyrate,
    result: searchCurrencyrateOpts
  } = useCurrencySearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  return (
    <>
      <CountryPage
        country={null}
        disabled={countryCreateOpts.loading}
        errors={countryCreateOpts.data?.countryCreate.errors || []}
        onBack={() => navigate(countryListUrl())}
        onDelete={undefined}
        onSubmit={handleSubmit}
        onValueAdd={() => openModal("add-value")}
        onValueDelete={id =>
          openModal("remove-value", {
            id
          })
        }
        onValueReorder={handleRegionsReorder}
        onValueUpdate={id =>
          openModal("edit-value", {
            id
          })
        }
        languages={
          searchLanguageOpts.data?.search?.edges.map(edge => edge.node) || []
        }
        fetchLanguages={searchLanguage}
        fetchMoreLanguages={{
          hasMore: searchLanguageOpts.data?.search?.pageInfo.hasNextPage,
          loading: searchLanguageOpts.loading,
          onFetchMore: loadMoreLanguages
        }}
        currencies={
          searchCurrencyrateOpts.data?.search?.edges.map(edge => edge.node) ||
          []
        }
        fetchCurrencyrates={searchCurrencyrate}
        fetchMoreCurrencyrates={{
          hasMore: searchCurrencyrateOpts.data?.search?.pageInfo.hasNextPage,
          loading: searchCurrencyrateOpts.loading,
          onFetchMore: loadMoreCurrencyrates
        }}
        saveButtonBarState={countryCreateOpts.status}
        regions={values.map((value, valueIndex) => ({
          __typename: "Region" as "Region",
          id: valueIndex.toString(),
          enable: value.reenable,
          ...value
        }))}
      />
      <RegionEditDialog
        regions={null}
        confirmButtonState="default"
        disabled={false}
        errors={valueErrors}
        open={params.action === "add-value"}
        onClose={closeModal}
        onSubmit={handleRegionsCreate}
        textChange={setTextChange}
      />
      {values.length > 0 && (
        <>
          <RegionDeleteDialog
            open={params.action === "remove-value"}
            name={getStringOrPlaceholder(values[id]?.name)}
            confirmButtonState="default"
            onClose={closeModal}
            onConfirm={handleRegionsDelete}
          />
          <RegionEditDialog
            regions={values[params.id]}
            confirmButtonState="default"
            disabled={false}
            errors={valueErrors}
            open={params.action === "edit-value"}
            onClose={closeModal}
            onSubmit={handleRegionsUpdate}
            textChange={setTextChange}
          />
        </>
      )}
    </>
  );
};

export default CountryDetails;
